import { useSessionContext } from '@/auth/useSessionContext';
import { COMPANY_STATUS_LABELS } from '@/consts/financial';
import {
  getRegistrationStatus,
  REGISTRATION_VALIDATION_STATUS,
  resolveRegistrationValidationStatus,
} from '@/sections/@dashboard/identity-verification/registrationValidationStatus';
import useCompanyData from './useCompanyData';

/**
 * Indica se o cadastro financeiro ainda precisa ser concluído.
 *
 * Fonte única: `companyData`. `user.companyStatus` e `companyData.status` são o
 * mesmo campo no backend (`Company.status`, em `user/serializers.py`), então
 * eles não divergem por conteúdo — só por frescor: o payload do usuário vem da
 * sessão, buscado no login e cacheado, enquanto o `companyData` é refetchado.
 *
 * Antes as duas fontes eram combinadas com `||`, o que dava ao valor velho o
 * poder de vencer: bastava a sessão dizer `approved` para `isPendingRegistration`
 * virar `false` e a tela de cadastro sumir, mesmo com o `companyData` fresco
 * dizendo `pending`.
 */
export const usePendingRegistration = () => {
  const { user } = useSessionContext();
  const { companyData } = useCompanyData();
  const isCompanyApproved = companyData?.status === COMPANY_STATUS_LABELS.APPROVED;
  const isPendingRegistration =
    companyData?.pendingRegistrationBanner === true && !isCompanyApproved;
  const hasSales = Number(user?.totalSales || 0) > 0;
  const registrationValidationStatus = resolveRegistrationValidationStatus({
    registrationStatus: getRegistrationStatus({ companyData }),
  });
  // `NOT_STARTED` é o `pre_approved`: pré-cadastro pronto e validação cadastral
  // via CAF não iniciada.
  const hasNotStartedRegistrationValidation =
    registrationValidationStatus === REGISTRATION_VALIDATION_STATUS.NOT_STARTED;

  return {
    isPendingRegistration,
    hasSales,
    // RF-03: venda realizada e validação cadastral não realizada. Não exige ter
    // postergado o convite — a História fala em venda feita e validação não
    // feita, sem condicionar a ter visto o modal — e não depende do
    // `pendingRegistrationBanner`, que descreve o pré-cadastro, não a CAF.
    shouldShowPendingRegistrationAlert: hasSales && hasNotStartedRegistrationValidation,
  };
};
